import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { SEO } from '../../utils/seo';

export default function AntiBriberyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Anti-Bribery & Corruption Policy - LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
    "description": "Anti-Bribery & Corruption Policy for LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS. Our commitment to ethical business practices and compliance with UK Bribery Act 2010.",
    "url": `${window.location.origin}/anti-bribery-policy`
  };

  return (
    <>
      <SEO
        title="Anti-Bribery & Corruption Policy | LEWIS SECURITY North Wales"
        description="LEWIS SECURITY operates a zero-tolerance approach to bribery and corruption. Learn about our commitment to ethical business practices and compliance with UK Bribery Act 2010."
        keywords="anti-bribery policy, corruption policy, UK Bribery Act 2010, ethical business, compliance, integrity"
        schema={schema}
        canonical={`${window.location.origin}/anti-bribery-policy`}
      />
      <div className="min-h-screen bg-white font-inter">
        <Navbar />
        
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">Anti-Bribery &amp; Corruption Policy</h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

              <section className="mb-10">
                <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-8">
                  <p className="text-lg font-semibold text-gray-900 mb-2">Zero-Tolerance Approach</p>
                  <p className="text-gray-700 leading-relaxed">
                    Lewis Security, Risk &amp; Compliance Solutions operates a <strong>zero-tolerance approach</strong> to bribery, corruption, facilitation payments, or unethical inducements.
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We are committed to maintaining the highest standards of ethical conduct in all our business activities. This policy applies to all employees, contractors, subcontractors, and business partners.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4 bg-gray-50 p-5 rounded-lg">
                    <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Fair &amp; Transparent Business Conduct</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Conducting business fairly, transparently, and with integrity in all our dealings with clients, partners, and stakeholders.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-gray-50 p-5 rounded-lg">
                    <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-close-circle-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Bribes or Improper Payments</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Not offering, giving, requesting, or accepting bribes or improper payments of any kind, regardless of value or circumstance.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-gray-50 p-5 rounded-lg">
                    <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-shield-check-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Avoiding Conflicts of Interest</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Avoiding conflicts of interest in awarding subcontracted work and ensuring all business decisions are made objectively and in the best interests of our clients.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-gray-50 p-5 rounded-lg">
                    <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-gift-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Reasonable Gifts &amp; Hospitality</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Ensuring any gifts, hospitality or expenses are reasonable, proportionate, and properly documented. All such activities must be transparent and comply with our internal guidelines.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-gray-50 p-5 rounded-lg">
                    <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-file-list-3-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Legal Compliance</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Complying with the <strong>UK Bribery Act 2010</strong> and all relevant legislation governing anti-bribery and corruption practices.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-gray-50 p-5 rounded-lg">
                    <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-team-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Partner Standards</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Expecting the same standards from subcontractors, delivery partners and suppliers. All third parties working with us must adhere to equivalent ethical standards.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">What Constitutes Bribery?</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Bribery is the offering, promising, giving, accepting or soliciting of an advantage as an inducement for action which is illegal or a breach of trust. This includes:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Offering or accepting cash payments to influence business decisions</li>
                  <li>Providing excessive gifts or entertainment to secure contracts</li>
                  <li>Facilitation payments to expedite routine government actions</li>
                  <li>Kickbacks or secret commissions</li>
                  <li>Political or charitable donations used as a disguise for bribery</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Reporting Concerns</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We encourage all employees, contractors, and business partners to report any suspicions of bribery or unethical behaviour. Reports can be made confidentially and without fear of retaliation.
                </p>
                <div className="bg-red-50 border-l-4 border-red-600 p-6">
                  <p className="text-gray-900 font-semibold mb-2">
                    <i className="ri-alert-line text-red-600 mr-2"></i>
                    Consequences of Non-Compliance
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Any suspicion of bribery or unethical behaviour will be investigated thoroughly and may result in <strong>immediate termination of partnership</strong> or <strong>referral to relevant authorities</strong>, including law enforcement.
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Training &amp; Awareness</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  All employees receive regular training on anti-bribery and corruption policies. We ensure that everyone understands their responsibilities and the consequences of non-compliance.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Policy Review</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This policy is reviewed regularly to ensure it remains effective and compliant with current legislation. Updates will be communicated to all relevant parties.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions about this policy or wish to report a concern, please contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 mb-2"><strong>LEWIS SECURITY, RISK &amp; COMPLIANCE SOLUTIONS</strong></p>
                  <p className="text-gray-700 mb-2">Email: <a href="mailto:info@jl-security.co.uk" className="text-teal-600 hover:text-teal-700 font-medium">info@jl-security.co.uk</a></p>
                  <p className="text-gray-700">Phone: <a href="tel:+447834561015" className="text-teal-600 hover:text-teal-700 font-medium">+44 7834 561015</a></p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Further Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For more information about the UK Bribery Act 2010, please visit:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 mb-2">
                    <a href="https://www.gov.uk/government/publications/bribery-act-2010-guidance" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 font-medium">
                      UK Government - Bribery Act 2010 Guidance
                    </a>
                  </p>
                  <p className="text-gray-700">
                    <a href="https://www.sfo.gov.uk/publications/guidance-policy-and-protocols/bribery-act-guidance/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 font-medium">
                      Serious Fraud Office - Bribery Act Guidance
                    </a>
                  </p>
                </div>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <Link 
                to="/" 
                className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}